import { Select, Skeleton } from "antd";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { formatDate } from "../../services/util";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

const { Option } = Select;

const TICKET_COLOR = "var(--color-primary)";
const CHECKIN_COLOR = "var(--color-accent)";

function transformDualAxisData(rawData) {
    if (!rawData || rawData.length < 2) return { data: [], keys: [] };
    const [headers, ...rows] = rawData;
    const [xKey, ...seriesKeys] = headers;
    const data = rows.map((row) => {
        const obj = { [xKey]: String(row[0]) };
        seriesKeys.forEach((k, i) => {
            obj[k] = Number(row[i + 1]) || 0;
        });
        return obj;
    });
    return { data, keys: seriesKeys, xKey };
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "var(--color-bg-control)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
            }}
        >
            <p
                style={{
                    margin: 0,
                    fontWeight: 600,
                    marginBottom: 6,
                    color: "var(--color-text-secondary)",
                    fontSize: 11,
                }}
            >
                {label}
            </p>
            {payload.map((entry) => (
                <p
                    key={entry.name}
                    style={{ margin: "2px 0", color: entry.color }}
                >
                    {entry.name}:{" "}
                    <strong style={{ color: "var(--color-text)" }}>
                        {entry.value}
                    </strong>
                </p>
            ))}
        </div>
    );
};

const axisStyle = {
    tick: { fill: "var(--color-text-muted)", fontSize: 11 },
    axisLine: { stroke: "var(--color-border)" },
    tickLine: false,
};

function Chart1() {
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [chartData, setChartData] = useState([]);
    const [parsedData, setParsedData] = useState({
        data: [],
        keys: [],
        xKey: "",
    });
    const [hotel_id, setSelectedHotel] = useState("");
    const [component_id, setSelectedComponent] = useState("");
    const [formData, setFormData] = useState({
        staff_id,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        hotel_id,
        component_id,
    });

    const {
        status: chartStatus,
        data: chartRes,
        refetch: getChart,
    } = useQuery(
        "compoentAndCheckInsPerMonth",
        async () =>
            await HTTP("/analytics/getComponentsCheckins", { ...formData }),
        { enabled: false },
    );

    const {
        status: hotelOptionStatus,
        data: hotelOptionsData,
        refetch: getHotels,
    } = useQuery(
        "hotels",
        async () =>
            await HTTP("/dashboard/getHotels", { staff_id, hotel_type: "all" }),
        { enabled: false },
    );

    const {
        status: componentStatus,
        data: componentRes,
        refetch: getComponent,
    } = useQuery(
        "hubspot-components",
        async () => await HTTP("/dashboard/getJiraComponents", { staff_id }),
        { enabled: false },
    );

    useEffect(() => {
        getHotels();
        getComponent();
    }, []);

    useEffect(() => {
        if (formData.hotel_id !== "" && formData.component_id !== "")
            getChart();
    }, [formData]);

    useEffect(() => {
        setFormData((p) => ({ ...p, hotel_id }));
    }, [hotel_id]);
    useEffect(() => {
        setFormData((p) => ({ ...p, component_id }));
    }, [component_id]);

    useEffect(() => {
        if (componentStatus === "success")
            setSelectedComponent(componentRes.data[0].component_id);
    }, [componentRes]);

    useEffect(() => {
        if (hotelOptionStatus === "success")
            setSelectedHotel(hotelOptionsData.data[0].hotel_id);
    }, [hotelOptionsData]);

    useEffect(() => {
        const [s, e] = selectedDates;
        setFormData((p) => ({
            ...p,
            start_date: formatDate(s),
            end_date: formatDate(e),
        }));
    }, [selectedDates]);

    useEffect(() => {
        if (chartStatus === "success" && chartRes.data !== null) {
            setChartData(chartRes.data);
            setParsedData(transformDualAxisData(chartRes.data));
        }
    }, [chartStatus, chartRes]);

    const isReady = chartStatus === "success" && parsedData.data.length > 0;
    const [ticketKey, checkinKey] = parsedData.keys;

    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "20px 24px",
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 16,
                }}
            >
                <div
                    style={{
                        width: 3,
                        height: 18,
                        borderRadius: 2,
                        background: "var(--gradient-primary)",
                    }}
                />
                <span
                    style={{
                        color: "var(--color-text)",
                        fontWeight: 600,
                        fontSize: 15,
                    }}
                >
                    Component & Check-ins Per Month
                </span>
            </div>

            {/* Legend pills */}
            <div
                style={{
                    display: "flex",
                    gap: 16,
                    marginBottom: 16,
                    flexWrap: "wrap",
                }}
            >
                {[
                    {
                        color: TICKET_COLOR,
                        label: "Tickets",
                        axis: "Left axis",
                    },
                    {
                        color: CHECKIN_COLOR,
                        label: "Check-ins",
                        axis: "Right axis",
                    },
                ].map(({ color, label, axis }) => (
                    <div
                        key={label}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                        }}
                    >
                        <div
                            style={{
                                width: 24,
                                height: 3,
                                borderRadius: 2,
                                background: color,
                            }}
                        />
                        <span
                            style={{
                                fontSize: 12,
                                color: "var(--color-text-muted)",
                            }}
                        >
                            {label}
                        </span>
                        <span
                            style={{
                                fontSize: 10,
                                color: "var(--color-text-muted)",
                                background: "var(--color-bg-control)",
                                padding: "1px 6px",
                                borderRadius: 4,
                                border: "1px solid var(--color-border)",
                            }}
                        >
                            {axis}
                        </span>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div
                style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    background: "var(--color-bg-control)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    padding: "12px 16px",
                    marginBottom: 20,
                }}
            >
                <Select
                    onChange={(e) => setSelectedHotel(e)}
                    value={hotel_id}
                    loading={hotelOptionStatus !== "success"}
                    showSearch
                    placeholder="Select Hotel"
                    style={{ flex: 1, minWidth: 200 }}
                    filterOption={(input, option) =>
                        option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                >
                    {hotelOptionStatus === "success" &&
                        hotelOptionsData.data.map((h) => (
                            <Option key={h.hotel_id} value={h.hotel_id}>
                                {`${h.hotel_id} - ${h.hotel_name}`}
                            </Option>
                        ))}
                </Select>
                <Select
                    onChange={(e) => setSelectedComponent(e)}
                    value={component_id}
                    loading={componentStatus !== "success"}
                    placeholder="Select Component"
                    style={{ flex: 1, minWidth: 200 }}
                    filterOption={(input, option) =>
                        option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                    showSearch
                >
                    {componentStatus === "success" &&
                        componentRes.data.map((c) => (
                            <Option key={c.component_id} value={c.component_id}>
                                {c.component_name}
                            </Option>
                        ))}
                </Select>
            </div>

            {/* Chart */}
            {isReady ? (
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                        data={parsedData.data}
                        margin={{ top: 10, right: 40, left: 0, bottom: 5 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="var(--color-border-light)"
                            vertical={false}
                        />
                        <XAxis dataKey={parsedData.xKey} {...axisStyle} />
                        <YAxis
                            yAxisId="left"
                            {...axisStyle}
                            label={{
                                value: "Tickets",
                                angle: -90,
                                position: "insideLeft",
                                fill: "var(--color-text-muted)",
                                fontSize: 11,
                            }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            {...axisStyle}
                            label={{
                                value: "Check-ins",
                                angle: 90,
                                position: "insideRight",
                                fill: "var(--color-text-muted)",
                                fontSize: 11,
                            }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                            formatter={(v) => (
                                <span
                                    style={{
                                        color: "var(--color-text-secondary)",
                                    }}
                                >
                                    {v}
                                </span>
                            )}
                        />
                        {ticketKey && (
                            <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey={ticketKey}
                                stroke={TICKET_COLOR}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 5 }}
                            />
                        )}
                        {checkinKey && (
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey={checkinKey}
                                stroke={CHECKIN_COLOR}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 5 }}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            ) : (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 8, width: "100%" }}
                />
            )}
        </div>
    );
}

export default Chart1;
